import { useTranslation } from 'react-i18next';

import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import Inventory2RoundedIcon from '@mui/icons-material/Inventory2Rounded';
import LocalOfferRoundedIcon from '@mui/icons-material/LocalOfferRounded';
import WarehouseRoundedIcon from '@mui/icons-material/WarehouseRounded';
import {
  Box,
  Button,
  ButtonBase,
  Chip,
  Divider,
  Drawer,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';

import { formatDateTime, formatPrice } from '../../lib/format';
import { computeReadiness } from '../../lib/readiness';
import {
  COMMERCIAL_AVAILABILITY_LABELS,
  PRICE_STATUS_LABELS,
  STOCK_STATUS_LABELS,
  commercialAvailabilityColor,
  stockStatusColor,
} from '../../model/options';
import type { Product, ProductSummary } from '../../model/types';
import { ReadinessChecklist } from './ReadinessChecklist';

// ── Props ────────────────────────────────────────────────────────────────────

export interface ProductDetailDrawerProps {
  /** Product to display. `null` means the drawer is not mounted. */
  product: ProductSummary | null;
  open: boolean;
  /** Whether the current user has `catalog.manage` permission. */
  canManage: boolean;
  /**
   * Pricing RBAC for the footer's "Price" button (design spec §4, adr-pricing-025 d9) —
   * INDEPENDENT of `canManage` (Phase-3 review F2): a `catalog.pricing.manage` holder without
   * `catalog.manage` still gets a working button, and a `catalog.manage` holder with no pricing
   * permission sees it disabled with a reason instead of a silently no-op enabled one. Mirrors the
   * products-list row action's own gate (`ProductsPage.tsx`'s `priceActionMode`) — `manage`/`view`
   * show the button (`view` opens the dialog read-only, decided by the caller), `none` disables it.
   */
  priceActionMode: 'manage' | 'view' | 'none';
  /**
   * Already-translated provenance caption for the price row (design spec §8.11/§12) — the same
   * caption the products-list price column shows, computed by `ProductsPage` (which alone holds
   * the current scope + the registries needed for names). `undefined` in the plain company scope
   * (no caption, byte-identical to today) or while the drawer has no provenance to explain.
   */
  priceProvenanceCaption?: string;
  onClose: () => void;
  /** Navigate to the full ProductEditPage for this product. */
  onEdit: (productId: string) => void;
  /**
   * Open the quick-price dialog for THIS product (design spec §8.12) — the full summary, not a
   * mapped-down `Product`, because the caller needs the row's own `*Used` provenance to derive the
   * scoped editor context ("edit what you see").
   */
  onQuickPrice: (product: ProductSummary) => void;
  /** Open the quick-stock dialog for this product. */
  onQuickStock: (product: Product) => void;
  /**
   * "Open the card" may deep-link `?drawer=prices` when the merchant came for the price (design
   * spec §12.7) — fired when the price row itself (not the generic Edit/Open button) is activated.
   * Optional: omitting it renders the price row as plain (non-interactive) text, unchanged.
   */
  onOpenPriceCard?: (productId: string) => void;
}

// ── Component ────────────────────────────────────────────────────────────────

/**
 * Right-side product-detail panel that opens on row click from `ProductsPage`.
 *
 * Primary content (above the fold):
 *   - Hero product image
 *   - Readiness checklist (cardStatus · commercialAvailability · published · price)
 *   - Overall readiness verdict alert
 *
 * Secondary content (scroll to see):
 *   - Price / old price
 *   - Category, stock status, channels, updated date
 *
 * Footer actions:
 *   - Quick price edit | Quick stock edit | Open full editor (canManage)
 *   - Open full page (view-only)
 *
 * The drawer does not fetch additional data — it uses the `ProductSummary`
 * already in the page list, making it zero-latency to open.
 */
export function ProductDetailDrawer({
  product,
  open,
  canManage,
  priceActionMode,
  priceProvenanceCaption,
  onClose,
  onEdit,
  onQuickPrice,
  onQuickStock,
  onOpenPriceCard,
}: ProductDetailDrawerProps) {
  const { t } = useTranslation('catalog');
  if (!product) return null;

  // Same three-way copy the products-list row action uses (design spec §4/§8.12, R2 fix) — kept
  // as one switch here so the footer button and its tooltip never drift from each other.
  const priceActionTooltip =
    priceActionMode === 'manage'
      ? t('products.rowActions.editPrice')
      : priceActionMode === 'view'
        ? t('products.rowActions.viewPrice')
        : t('products.rowActions.editPriceNoAccess');

  const readiness = computeReadiness({
    cardStatus: product.cardStatus,
    commercialAvailability: product.commercialAvailability,
    published: product.published ?? false,
    publishedChannels: product.publishedChannels,
    price: product.price,
  });

  /** Minimal Product shape for quick-edit dialogs (uses only subset of fields). */
  const asProduct: Product = {
    id: product.id,
    name: product.name,
    code: product.code,
    categoryId: product.categoryId ?? '',
    categoryName: product.categoryName ?? '',
    brand: product.brand,
    price: product.price,
    priceStatus: product.priceStatus,
    imageUrl: product.imageUrl,
    stockStatus: (product.stockStatus ?? 'CHECK_AVAILABILITY') as Product['stockStatus'],
    cardStatus: product.cardStatus,
    commercialAvailability: product.commercialAvailability,
  };

  const hasSecondaryContent =
    product.categoryName ||
    product.stockStatus ||
    (product.publishedChannels && product.publishedChannels.length > 0) ||
    product.updatedAt;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      slotProps={{
        paper: {
          sx: {
            width: { xs: '100vw', sm: 480 },
            display: 'flex',
            flexDirection: 'column',
          },
        },
      }}
    >
      {/* ── Header (sticky) ─────────────────────────────────────────────── */}
      <Box
        sx={{
          px: 2.5,
          py: 1.75,
          borderBottom: (t) => `1px solid ${t.palette.divider}`,
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 1.5,
          flexShrink: 0,
          bgcolor: 'background.paper',
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography
            variant="h6"
            sx={{ fontSize: '1rem', fontWeight: 600, lineHeight: 1.35 }}
            noWrap
          >
            {product.name}
          </Typography>
          {(product.code || product.brand) && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mt: 0.25, display: 'block' }}
              noWrap
            >
              {product.code && (
                <Box component="span" sx={{ fontFamily: 'JetBrains Mono, monospace' }}>
                  {product.code}
                </Box>
              )}
              {product.code && product.brand ? ' · ' : ''}
              {product.brand}
            </Typography>
          )}
        </Box>
        <IconButton
          onClick={onClose}
          size="small"
          aria-label={t('productDetailDrawer.closeAria')}
          sx={{ flexShrink: 0, mt: -0.25 }}
        >
          <CloseRoundedIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* ── Body (scrollable) ──────────────────────────────────────────────── */}
      <Box sx={{ flex: 1, overflowY: 'auto' }}>
        {/* Hero image */}
        <Box
          sx={{
            width: '100%',
            aspectRatio: '16 / 9',
            bgcolor: 'action.hover',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden',
            flexShrink: 0,
          }}
        >
          {product.imageUrl ? (
            <Box
              component="img"
              src={product.imageUrl}
              alt={product.name}
              sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
            />
          ) : (
            <Inventory2RoundedIcon sx={{ fontSize: 56, color: 'action.disabled' }} />
          )}
        </Box>

        {/* ── Readiness panel ─── PRIMARY CONTENT ───────────────────────── */}
        <Stack spacing={1.25} sx={{ px: 2.5, pt: 2.5, pb: 2 }}>
          <Typography
            variant="overline"
            color="text.secondary"
            sx={{ lineHeight: 1.2, letterSpacing: '0.08em' }}
          >
            {t('productDetailDrawer.readinessLabel')}
          </Typography>

          <ReadinessChecklist readiness={readiness} />
        </Stack>

        {/* ── Price ────────────── SECONDARY CONTENT ───────────────────── */}
        <Divider />

        {(() => {
          const priceRowContent = (
            <>
              <Typography variant="body2" color="text.secondary">
                {t('productDetailDrawer.priceRowLabel')}
              </Typography>
              <Box textAlign="right">
                {/* Governed price enrichment (plan-catalog-007): amount when ACTIVE, else the
                    governance/degradation state as muted text. */}
                <Typography variant="body2" fontWeight={700}>
                  {product.price != null ? formatPrice(product.price) : '—'}
                </Typography>
                {product.price == null && product.priceStatus ? (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    {product.priceStatus === 'UNAVAILABLE'
                      ? t('products.priceUnavailable')
                      : PRICE_STATUS_LABELS[product.priceStatus]
                        ? t(PRICE_STATUS_LABELS[product.priceStatus])
                        : product.priceStatus}
                  </Typography>
                ) : null}
                {/* Provenance caption (design spec §8.11/§12) — the scoped list's own column
                    caption, mirrored here; only ever set alongside a resolved (ACTIVE) price. */}
                {priceProvenanceCaption ? (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    {priceProvenanceCaption}
                  </Typography>
                ) : null}
              </Box>
            </>
          );
          return onOpenPriceCard ? (
            <ButtonBase
              onClick={() => onOpenPriceCard(product.id)}
              aria-label={t('productDetailDrawer.openPriceCardAria', { name: product.name })}
              sx={{
                width: '100%',
                justifyContent: 'space-between',
                px: 2.5,
                py: 1.75,
                textAlign: 'left',
                '&:hover': { bgcolor: 'action.hover' },
                '&:focus-visible': {
                  outline: (theme) => `2px solid ${theme.palette.primary.main}`,
                  outlineOffset: '-2px',
                },
              }}
            >
              {priceRowContent}
            </ButtonBase>
          ) : (
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              sx={{ px: 2.5, py: 1.75 }}
            >
              {priceRowContent}
            </Stack>
          );
        })()}

        {/* ── Details ──────────────────────────────────────────────────── */}
        {hasSecondaryContent && (
          <>
            <Divider />
            <Stack spacing={0} divider={<Divider />}>
              {product.categoryName && (
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  spacing={2}
                  sx={{ px: 2.5, py: 1.5 }}
                >
                  <Typography variant="body2" color="text.secondary" sx={{ flexShrink: 0 }}>
                    {t('productDetailDrawer.categoryLabel')}
                  </Typography>
                  <Typography variant="body2" sx={{ textAlign: 'right' }}>
                    {product.categoryName}
                  </Typography>
                </Stack>
              )}

              {product.stockStatus && (
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  sx={{ px: 2.5, py: 1.25 }}
                >
                  <Typography variant="body2" color="text.secondary">
                    {t('productDetailDrawer.stockLabel')}
                  </Typography>
                  <Chip
                    size="small"
                    variant="outlined"
                    color={stockStatusColor(product.stockStatus)}
                    label={
                      STOCK_STATUS_LABELS[product.stockStatus]
                        ? t(STOCK_STATUS_LABELS[product.stockStatus])
                        : product.stockStatus
                    }
                  />
                </Stack>
              )}

              {product.commercialAvailability && (
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  sx={{ px: 2.5, py: 1.25 }}
                >
                  <Typography variant="body2" color="text.secondary">
                    {t('productDetailDrawer.availabilityLabel')}
                  </Typography>
                  <Chip
                    size="small"
                    variant="outlined"
                    color={commercialAvailabilityColor(product.commercialAvailability)}
                    label={
                      COMMERCIAL_AVAILABILITY_LABELS[product.commercialAvailability]
                        ? t(COMMERCIAL_AVAILABILITY_LABELS[product.commercialAvailability])
                        : product.commercialAvailability
                    }
                  />
                </Stack>
              )}

              {product.publishedChannels && product.publishedChannels.length > 0 && (
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  spacing={1}
                  sx={{ px: 2.5, py: 1.25 }}
                >
                  <Typography variant="body2" color="text.secondary" sx={{ flexShrink: 0 }}>
                    {t('productDetailDrawer.channelsLabel')}
                  </Typography>
                  <Stack
                    direction="row"
                    spacing={0.5}
                    flexWrap="wrap"
                    useFlexGap
                    justifyContent="flex-end"
                  >
                    {product.publishedChannels.map((ch) => (
                      <Chip key={ch} size="small" variant="outlined" color="success" label={ch} />
                    ))}
                  </Stack>
                </Stack>
              )}

              {product.updatedAt && (
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  sx={{ px: 2.5, py: 1.5 }}
                >
                  <Typography variant="body2" color="text.secondary">
                    {t('productDetailDrawer.updatedLabel')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {formatDateTime(product.updatedAt)}
                  </Typography>
                </Stack>
              )}
            </Stack>
          </>
        )}
      </Box>

      {/* ── Footer actions (sticky) ───────────────────────────────────────── */}
      <Box
        sx={{
          px: 2.5,
          py: 1.75,
          borderTop: (t) => `1px solid ${t.palette.divider}`,
          flexShrink: 0,
          bgcolor: 'background.paper',
        }}
      >
        <Stack direction="row" spacing={1}>
          {/*
           * "Price" is gated on `priceActionMode`, INDEPENDENT of `canManage` (design spec §4,
           * Phase-3 review F2/R2): a pricing-only manager who lacks `catalog.manage` still gets a
           * working button here (today's regression vs. the list's own row action), and a
           * catalog editor with no pricing permission sees it disabled with the same explanatory
           * tooltip instead of a silently no-op enabled one. The `span` wrapper is required for a
           * Tooltip on a potentially-disabled Button (design spec §13).
           */}
          <Tooltip title={priceActionTooltip}>
            <span style={{ flex: 1 }}>
              <Button
                fullWidth
                variant="outlined"
                size="small"
                startIcon={<LocalOfferRoundedIcon fontSize="small" />}
                disabled={priceActionMode === 'none'}
                onClick={() => onQuickPrice(product)}
              >
                {t('productDetailDrawer.priceButton')}
              </Button>
            </span>
          </Tooltip>
          {canManage ? (
            <>
              <Button
                variant="outlined"
                size="small"
                startIcon={<WarehouseRoundedIcon fontSize="small" />}
                onClick={() => onQuickStock(asProduct)}
                sx={{ flex: 1 }}
              >
                {t('productDetailDrawer.stockButton')}
              </Button>
              <Button
                variant="contained"
                size="small"
                startIcon={<EditRoundedIcon fontSize="small" />}
                onClick={() => onEdit(product.id)}
                sx={{ flex: 2 }}
              >
                {t('productDetailDrawer.editButton')}
              </Button>
            </>
          ) : (
            <Button variant="outlined" size="small" onClick={() => onEdit(product.id)} sx={{ flex: 2 }}>
              {t('productDetailDrawer.openButton')}
            </Button>
          )}
        </Stack>
      </Box>
    </Drawer>
  );
}
